<template>
  <div class="step-datetime">
    <h2 class="step-datetime__title">Escolha a data e hora</h2>

    <div v-if="service" class="step-datetime__service-info mb-3">
      <div class="step-datetime__service-line">
        <div class="step-datetime__service-main">
          <strong>{{ service.name }}</strong>
          <span v-if="meetingDurationText" class="ml-2">
            <i class="pi pi-clock" /> {{ meetingDurationText }}
          </span>
          <span v-if="modeLabel" class="step-datetime__mode-badge ml-2">{{
            modeLabel
          }}</span>
        </div>
        <div
          v-if="appointmentMode === 'on-site' && officeOptions.length > 1"
          class="step-datetime__office-inline"
        >
          <label
            class="step-datetime__office-label flex mr-2 mb-0"
            for="presencial-office"
            >Balcão presencial</label
          >
          <Dropdown
            id="presencial-office"
            input-id="presencial-office-input"
            :model-value="officeId"
            :options="officeOptions"
            option-label="label"
            option-value="id"
            placeholder="Escolha o escritório"
            class="step-datetime__office-dropdown"
            @update:model-value="$emit('office-change', $event)"
          />
        </div>
      </div>
    </div>

    <div v-if="blockPresencialNoOffices" class="step-datetime__no-office">
      <i class="pi pi-building" />
      <p>{{ officeGateMessage }}</p>
    </div>

    <div
      v-else-if="needsPresencialOfficeChoice"
      class="step-datetime__office-choice-only"
    >
      <i class="pi pi-building" />
      <p>{{ officeGateMessage }}</p>
      <label class="step-datetime__office-label" for="presencial-office-choice"
        >Balcão presencial</label
      >
      <Dropdown
        id="presencial-office-choice"
        input-id="presencial-office-choice-input"
        :model-value="officeId"
        :options="officeOptions"
        option-label="label"
        option-value="id"
        placeholder="Escolha o escritório"
        class="w-full"
        @update:model-value="$emit('office-change', $event)"
      />
    </div>

    <div v-else-if="isLoadingDates" class="step-datetime__loading">
      <ProgressBar mode="indeterminate" />
      <p class="mt-2 text-center">A verificar disponibilidade...</p>
    </div>

    <div v-else-if="noAvailability" class="step-datetime__no-availability">
      <i class="pi pi-calendar-times" />
      <p>Sem disponibilidade para este serviço. Por favor contacte-nos.</p>
    </div>

    <div v-else class="step-datetime__content step-datetime__content--stacked">
      <div v-if="suggestedSlots.length > 0" class="step-datetime__suggestions">
        <p class="step-datetime__suggestions-title">
          <i class="pi pi-bolt" />
          Horários sugeridos
        </p>
        <div class="step-datetime__suggestions-grid">
          <button
            v-for="slot in suggestedSlots"
            :key="slot.startDateUTC"
            type="button"
            class="step-datetime__suggestion-card"
            :class="{
              'step-datetime__suggestion-card--selected':
                selectedDateStr === slot.date && selectedTime === slot.time,
            }"
            @click="selectSlot(slot.date, slot.time)"
          >
            <span class="step-datetime__suggestion-day">{{
              slot.dayLabel
            }}</span>
            <strong class="step-datetime__suggestion-hour">{{
              slot.localHour
            }}</strong>
          </button>
        </div>
      </div>

      <div v-if="hasCalendarRestriction" class="step-datetime__calendar-action">
        <Button
          type="button"
          :label="
            showCalendarForLater
              ? 'Ocultar calendário de horários seguintes'
              : 'Caso pretenda outro horário clique neste botão'
          "
          class="p-button-outlined"
          @click="showCalendarForLater = !showCalendarForLater"
        />
        <small>
          O calendário mostra apenas horários posteriores às 12 sugestões acima.
        </small>
      </div>

      <div
        v-if="showCalendarForLater || !hasCalendarRestriction"
        class="step-datetime__calendar-and-times"
      >
        <div class="step-datetime__calendar">
          <Calendar
            v-model="selectedDate"
            :inline="true"
            :min-date="calendarMinDate"
            :max-date="maxDate"
            :disabled-dates="disabledDateObjects"
            :disabled-days="[]"
            date-format="yy-mm-dd"
            class="appointment-calendar"
            @date-select="onDateSelect"
          />
        </div>

        <div v-if="selectedDate" class="step-datetime__times">
          <p class="step-datetime__times-title">
            <i class="pi pi-calendar" />
            {{ formatSelectedDate }}
          </p>

          <div
            v-if="availableTimesForDay.length === 0"
            class="step-datetime__no-times"
          >
            <i class="pi pi-clock" />
            <p>Sem horários disponíveis neste dia.<br />Escolha outra data.</p>
          </div>

          <template v-else>
            <div v-if="morningSlots.length > 0" class="time-period">
              <div class="time-period__header">
                <i class="pi pi-sun" />
                <span>Manhã</span>
              </div>
              <div class="time-period__grid">
                <button
                  v-for="slot in morningSlots"
                  :key="slot.hour"
                  type="button"
                  class="time-slot"
                  :class="{ 'time-slot--selected': selectedTime === slot.hour }"
                  @click="selectTime(slot.hour)"
                >
                  <span class="time-slot__hour">{{ slot.hour }}</span>
                </button>
              </div>
            </div>

            <div v-if="afternoonSlots.length > 0" class="time-period">
              <div class="time-period__header">
                <i class="pi pi-moon" />
                <span>Tarde</span>
              </div>
              <div class="time-period__grid">
                <button
                  v-for="slot in afternoonSlots"
                  :key="slot.hour"
                  type="button"
                  class="time-slot"
                  :class="{ 'time-slot--selected': selectedTime === slot.hour }"
                  @click="selectTime(slot.hour)"
                >
                  <span class="time-slot__hour">{{ slot.hour }}</span>
                </button>
              </div>
            </div>
          </template>
        </div>

        <div
          v-else
          class="step-datetime__times step-datetime__times--placeholder"
        >
          <i class="pi pi-arrow-left" />
          <p>Seleccione uma data para ver os horários disponíveis.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import "dayjs/locale/pt";
dayjs.locale("pt");

export default {
  name: "AppointmentStepDateTime",
  props: {
    service: { type: Object, default: null },
    /** 'online' | 'on-site' — define filtros da API de datas (ex. officeId presencial) */
    appointmentMode: { type: String, default: null },
    /** Escritório do cliente, para disponibilidade presencial */
    officeId: { type: Number, default: null },
    /** Sem balcões com agenda para o serviço (região do cliente) */
    blockPresencialNoOffices: { type: Boolean, default: false },
    /** Vários balcões: obrigar escolha antes do calendário */
    needsPresencialOfficeChoice: { type: Boolean, default: false },
    /** Texto de ajuda para os estados presenciais acima */
    officeGateMessage: { type: String, default: "" },
    /** Opções { id, label } para escolha de balcão (>1 balcões) */
    officeOptions: {
      type: Array,
      default: function () {
        return [];
      },
    },
    modelValue: { type: Object, default: null }, // { date: 'YYYY-MM-DD', time: 'HH:mm', startDateUTC: 'YYYY-MM-DD HH:mm' }
    isFromActivation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "valid", "office-change"],
  data() {
    return {
      availableDays: [],
      disabledDates: [],
      isLoadingDates: false,
      noAvailability: false,
      selectedDate: null,
      selectedTime: null,
      showCalendarForLater: false,
      today: new Date(),
      maxDate: dayjs().add(12, "month").toDate(),
      lastAvailabilityContext: null,
    };
  },
  computed: {
    disabledDateObjects() {
      return this.disabledDates.map(function (d) {
        var parts = d.split("-");
        return new Date(
          parseInt(parts[0]),
          parseInt(parts[1]) - 1,
          parseInt(parts[2])
        );
      });
    },
    selectedDateStr() {
      if (!this.selectedDate) return null;
      return dayjs(this.selectedDate).format("YYYY-MM-DD");
    },
    availableTimesForDay() {
      if (!this.selectedDate) return [];
      const dateStr = this.selectedDateStr;
      const day = this.availableDays.find((d) => d.date === dateStr);
      return day ? day.times : [];
    },
    formatSelectedDate() {
      if (!this.selectedDate) return "";
      return dayjs(this.selectedDate).format("dddd, DD [de] MMMM");
    },
    morningSlots() {
      return this.availableTimesForDay.filter(function (slot) {
        var h = parseInt(slot.hour.split(":")[0]);
        return h < 13;
      });
    },
    afternoonSlots() {
      return this.availableTimesForDay.filter(function (slot) {
        var h = parseInt(slot.hour.split(":")[0]);
        return h >= 13;
      });
    },
    meetingDurationText() {
      return this.service
        ? this.formatDuration(this.service.meetingDuration)
        : "";
    },
    modeLabel() {
      if (this.appointmentMode === "online") return "Online";
      if (this.appointmentMode === "on-site") return "Presencial";
      return "";
    },
    shouldBlockPresencialFetch() {
      if (this.appointmentMode !== "on-site") return false;
      if (this.blockPresencialNoOffices) return true;
      if (this.needsPresencialOfficeChoice) return true;
      return this.officeId == null;
    },
    flattenedSlots() {
      var all = [];
      this.availableDays.forEach((day) => {
        (day.times || []).forEach((timeSlot) => {
          var startDateUTC = day.date + " " + timeSlot.hour;
          all.push({
            date: day.date,
            time: timeSlot.hour,
            startDateUTC: startDateUTC,
            sortKey: dayjs(`${day.date} ${timeSlot.hour}`).valueOf(),
          });
        });
      });

      all.sort(function (a, b) {
        return a.sortKey - b.sortKey;
      });

      return all;
    },
    minSuggestedDate() {
      return this.addBusinessDays(dayjs().startOf("day"), 3);
    },
    eligibleSlotsForSuggestions() {
      var minTs = this.minSuggestedDate.valueOf();
      return this.flattenedSlots.filter(function (slot) {
        return slot.sortKey >= minTs;
      });
    },
    firstTwelveSlots() {
      return this.eligibleSlotsForSuggestions.slice(0, 12);
    },
    suggestedSlots() {
      return this.firstTwelveSlots.slice(0, 6).map((slot) => {
        return {
          ...slot,
          dayLabel: dayjs(slot.date).format("ddd, DD/MM"),
          localHour: slot.time,
        };
      });
    },
    hasCalendarRestriction() {
      return this.firstTwelveSlots.length === 12;
    },
    calendarMinDate() {
      if (!this.hasCalendarRestriction) return this.today;
      var lastSlot = this.firstTwelveSlots[this.firstTwelveSlots.length - 1];
      return dayjs(lastSlot.date).add(1, "day").startOf("day").toDate();
    },
  },
  watch: {
    shouldBlockPresencialFetch: {
      handler(blocked) {
        if (!blocked && this.service && this.appointmentMode) {
          this.fetchDates();
        }
        if (blocked) {
          this.$emit("valid", false);
        }
      },
    },
    service: {
      immediate: true,
      handler(newService) {
        if (
          newService &&
          this.appointmentMode &&
          !this.shouldBlockPresencialFetch
        )
          this.fetchDates();
      },
    },
    appointmentMode() {
      if (
        this.service &&
        this.appointmentMode &&
        !this.shouldBlockPresencialFetch
      )
        this.fetchDates();
    },
    officeId() {
      if (
        this.service &&
        this.appointmentMode === "on-site" &&
        !this.shouldBlockPresencialFetch
      )
        this.fetchDates();
    },
  },
  methods: {
    fetchDates() {
      if (!this.service || !this.appointmentMode) return;
      if (this.shouldBlockPresencialFetch) {
        this.isLoadingDates = false;
        this.noAvailability = false;
        this.availableDays = [];
        this.disabledDates = [];
        this.selectedDate = null;
        this.selectedTime = null;
        this.lastAvailabilityContext = null;
        this.$emit("update:modelValue", null);
        this.$emit("valid", false);
        return;
      }
      const ctx = `${this.service.id}|${this.appointmentMode}|${
        this.officeId != null ? this.officeId : ""
      }`;
      const contextChanged = this.lastAvailabilityContext !== ctx;
      this.lastAvailabilityContext = ctx;

      this.isLoadingDates = true;
      this.noAvailability = false;
      if (contextChanged) {
        this.availableDays = [];
        this.disabledDates = [];
        this.selectedDate = null;
        this.selectedTime = null;
        this.showCalendarForLater = false;
        this.$emit("update:modelValue", null);
        this.$emit("valid", false);
      }

      const params = { servicePriceId: this.service.id };
      if (this.appointmentMode === "on-site" && this.officeId != null) {
        params.officeId = this.officeId;
      }

      this.$root
        .apiGet(
          this.isFromActivation
            ? this.$root.api.appointmentDatesUnauth
            : this.$root.api.appointmentDates,
          {
            params,
          }
        )
        .then((response) => {
          const data = response.data.data;
          const rawDays = data.availableDays || [];
          this.availableDays = rawDays.map(function (day) {
            var hourKeys = [];
            var hoursMap = {};
            (day.hours || []).forEach(function (h) {
              var hStr =
                h.hour.toString().length === 1
                  ? "0" + h.hour
                  : h.hour.toString();
              if (!hoursMap[hStr]) {
                var firstMinute =
                  h.minutes && h.minutes.length > 0 ? h.minutes[0] : "00";
                hoursMap[hStr] = {
                  hour: hStr + ":" + firstMinute,
                  available: true,
                };
                hourKeys.push(hStr);
              }
            });
            hourKeys.sort();
            var times = hourKeys.map(function (k) {
              return hoursMap[k];
            });
            return { date: day.date, times: times };
          });
          this.disabledDates = data.disabledDates || [];
          if (this.availableDays.length === 0) {
            this.noAvailability = true;
          }
          this.syncSelectionFromModel();
        })
        .catch((error) => {
          this.$root.toastError(
            (error &&
              error.response &&
              error.response.data &&
              error.response.data.msg_code) ||
              "error"
          );
          this.noAvailability = true;
        })
        .finally(() => {
          this.isLoadingDates = false;
        });
    },
    onDateSelect(date) {
      this.selectedDate = date;
      this.selectedTime = null;
      this.$emit("valid", false);
    },
    selectSlot(dateStr, utcHour) {
      this.selectedDate = dayjs(dateStr).toDate();
      this.selectTime(utcHour);
    },
    selectTime(utcHour) {
      this.selectedTime = utcHour;
      const dateStr = this.selectedDateStr;
      const startDateUTC = `${dateStr} ${utcHour}`;
      this.$emit("update:modelValue", {
        date: dateStr,
        time: utcHour,
        startDateUTC,
      });
      this.$emit("valid", true);
    },
    syncSelectionFromModel() {
      if (!this.modelValue || !this.modelValue.date || !this.modelValue.time)
        return;
      const dateStr = this.modelValue.date;
      const day = this.availableDays.find((d) => d.date === dateStr);
      if (!day || !day.times) return;
      const hasTime = day.times.some((t) => t.hour === this.modelValue.time);
      if (!hasTime) return;
      this.selectedDate = dayjs(dateStr).toDate();
      this.selectedTime = this.modelValue.time;
      this.$emit("valid", true);
    },
    addBusinessDays(baseDate, daysToAdd) {
      var cursor = baseDate;
      var added = 0;

      while (added < daysToAdd) {
        cursor = cursor.add(1, "day");
        var weekDay = cursor.day();
        if (weekDay !== 0 && weekDay !== 6) {
          added += 1;
        }
      }

      return cursor;
    },
    formatDuration(duration) {
      if (!duration) return "";
      var d = String(duration);
      if (d === "00:00:00") return "";
      var h = 0;
      var m = 0;
      var isoMatch = d.match(/T(\d{2}):(\d{2}):(\d{2})/);
      if (isoMatch) {
        h = parseInt(isoMatch[1], 10);
        m = parseInt(isoMatch[2], 10);
      } else {
        var parts = d.split(":");
        h = parseInt(parts[0], 10);
        m = parseInt(parts[1], 10);
      }
      if (isNaN(h) || isNaN(m)) return "";
      if (h === 0 && m === 0) return "";
      if (h > 0 && m > 0) return h + "h " + m + "min";
      if (h > 0) return h + "h";
      return m + "min";
    },
  },
};
</script>
